import { EnvironmentInfo, VersionInfo } from "@abbvie-epi/common/models";
import { PlatformLocation } from "@angular/common";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, combineLatest, map, Observable, of, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { SentryLoader } from "../common/classes/SentryLoader";

@Injectable({
    providedIn: "root"
})
export class ConfigService {
    private httpClient: HttpClient;

    constructor(http: HttpClient, private handler: HttpBackend, private platformLocation: PlatformLocation) {
        this.httpClient = http;
    }

    public getConfig() {
        // Create new HTTP client without interceptors
        this.httpClient = new HttpClient(this.handler);
        return combineLatest([this.getVersionInfo(), this.getEnvironmentInfo()]).pipe(
            tap(([versionInfo, environmentInfo]) => {
                let { version, buildNumber, commitHash } = versionInfo;
                let { environmentName, apiHost } = environmentInfo;
                environmentName = environmentName || "development";
                buildNumber = buildNumber || "development";
                commitHash = commitHash || "development";
                apiHost = apiHost || "/";

                environment.environmentName = environmentName;
                environment.version = version;
                environment.buildNumber = buildNumber;
                environment.commitHash = commitHash;
                environment.apiHost = apiHost;

                console.log(`****** ENVIRONMENT: ${environment.environmentName} ******`);
                console.log(`****** VERSION: ${version} ******`);
                console.log(`****** BUILD: ${buildNumber} ******`);
                console.log(`****** COMMIT: ${commitHash} ******`);

                const baseHref = this.platformLocation.getBaseHrefFromDOM();

                const sentryLoader = new SentryLoader();
                sentryLoader.initLogging(baseHref);
            }),
            map(() => true)
        );
    }

    /**
     * Get version info from root directory
     */
    private getVersionInfo(): Observable<VersionInfo> {
        return this.httpClient
            .get<VersionInfo>("version.json", {
                responseType: "json"
            })
            .pipe(
                catchError((err, thrown) => {
                    console.error(err);
                    console.error("Unable to retrieve version.json");
                    const { version, buildNumber, commitHash } = environment;
                    return of({ version, buildNumber, commitHash });
                })
            );
    }

    /**
     * Get environment info from root directory
     */
    private getEnvironmentInfo(): Observable<EnvironmentInfo> {
        return this.httpClient
            .get<EnvironmentInfo>("environment.json", {
                responseType: "json"
            })
            .pipe(
                catchError((err, thrown) => {
                    console.error(err);
                    console.error("Unable to retrieve environment.json");
                    const { environmentName, apiHost } = environment;
                    return of({ environmentName, apiHost });
                })
            );
    }
}
